/**
 * Forgot Password Page
 * SCR-803: Forgot Password
 *
 * UX Intent Implementation:
 * - friction_intent: intentional (60s cooldown via CooldownButton)
 * - error_handling: blocking (via BlockingErrorState)
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CooldownButton, BlockingErrorState } from '@myvault/shared';
import { useForgotPassword, useResetPassword, useVerifyResetToken } from '../hooks/useAuth';
import { PASSWORD_REQUIREMENTS } from '../types';

type Step = 'request' | 'sent' | 'reset' | 'success' | 'expired' | 'error';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [logoutOthers, setLogoutOthers] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [resendTriggered, setResendTriggered] = useState(false);

  const forgotMutation = useForgotPassword();
  const resetMutation = useResetPassword();
  const { data: tokenData, isError: tokenInvalid } = useVerifyResetToken(token);

  useEffect(() => {
    if (token) {
      if (tokenInvalid) {
        setStep('expired');
      } else if (tokenData?.valid) {
        setStep('reset');
      }
    }
  }, [token, tokenData, tokenInvalid]);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await forgotMutation.mutateAsync({ email });
      setStep('sent');
    } catch (error) {
      console.error('Failed to request reset:', error);
    }
  };

  const handleResend = async () => {
    try {
      await forgotMutation.mutateAsync({ email });
      setResendTriggered(true);
    } catch (error) {
      console.error('Failed to resend:', error);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || newPassword !== confirmPassword) return;

    try {
      await resetMutation.mutateAsync({
        token,
        newPassword,
        confirmPassword,
        logoutOtherSessions: logoutOthers,
      });
      setStep('success');
    } catch (error) {
      console.error('Failed to reset password:', error);
    }
  };

  const getPasswordStrength = (password: string) => {
    let score = 0;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*]/.test(password)) score++;
    return score;
  };

  // Request Step
  if (step === 'request') {
    return (
      <div className="min-h-screen bg-mv-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-mv-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-mv-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-mv-gray-900 mb-2">Wachtwoord vergeten?</h1>
            <p className="text-mv-gray-600">
              Geen probleem. Voer je e-mailadres in en we sturen je een link om je wachtwoord te resetten.
            </p>
          </div>

          <form onSubmit={handleRequestReset} className="bg-white rounded-lg border border-mv-gray-200 p-8">
            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium text-mv-gray-700 mb-1">
                E-mailadres
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jan@voorbeeld.nl"
                className="w-full px-4 py-2 border border-mv-gray-300 rounded-lg focus:ring-2 focus:ring-mv-blue-600 focus:border-transparent"
                required
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={forgotMutation.isPending}
              className="w-full py-3 bg-mv-blue-600 text-white font-semibold rounded-lg hover:bg-mv-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {forgotMutation.isPending ? 'Versturen...' : 'Verstuur reset link'}
            </button>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-mv-blue-600 hover:text-mv-blue-900 font-medium inline-flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Terug naar inloggen
              </Link>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Sent Step
  if (step === 'sent') {
    return (
      <div className="min-h-screen bg-mv-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-mv-gray-900 mb-2">Controleer je inbox</h1>
            <p className="text-mv-gray-600 mb-2">We hebben een reset link gestuurd naar:</p>
            <p className="font-semibold text-mv-gray-900">{email}</p>
          </div>

          <div className="bg-white rounded-lg border border-mv-gray-200 p-6">
            <div className="bg-mv-blue-50 border-l-4 border-mv-blue-600 p-4 rounded mb-6">
              <p className="text-sm text-mv-blue-900">
                De link is 1 uur geldig. Controleer ook je spam-folder als je de e-mail niet ziet.
              </p>
            </div>

            {/* SCR-803 UX Intent: friction_intent: intentional - 60s cooldown */}
            <div className="text-center">
              <p className="text-sm text-mv-gray-600 mb-2">Geen e-mail ontvangen?</p>
              <CooldownButton
                cooldownSeconds={60}
                startInCooldown={resendTriggered}
                persistKey={`forgot-password-resend-${email}`}
                onClick={handleResend}
                cooldownText="Opnieuw versturen ({seconds}s)"
                availableText="Opnieuw versturen"
                variant="ghost"
                size="sm"
                className="text-mv-blue-600 hover:text-mv-blue-900"
              />
            </div>

            <div className="mt-6 pt-6 border-t border-mv-gray-200 text-center">
              <Link to="/login" className="text-sm text-mv-blue-600 hover:text-mv-blue-900 font-medium inline-flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Terug naar inloggen
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset Step
  if (step === 'reset') {
    return (
      <div className="min-h-screen bg-mv-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-mv-gray-900 mb-2">Nieuw wachtwoord instellen</h1>
            <p className="text-mv-gray-600">Kies een sterk wachtwoord voor je account</p>
          </div>

          <form onSubmit={handleResetPassword} className="bg-white rounded-lg border border-mv-gray-200 p-8">
            <div className="mb-4">
              <label htmlFor="newPassword" className="block text-sm font-medium text-mv-gray-700 mb-1">
                Nieuw wachtwoord
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimaal 12 tekens"
                  className="w-full px-4 py-2 pr-10 border border-mv-gray-300 rounded-lg focus:ring-2 focus:ring-mv-blue-600 focus:border-transparent"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-mv-gray-400 hover:text-mv-gray-600"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {newPassword && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded ${
                          i <= getPasswordStrength(newPassword)
                            ? 'bg-green-500'
                            : 'bg-mv-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <ul className="space-y-1">
                    {PASSWORD_REQUIREMENTS.map((req) => {
                      const met = req.id === 'length'
                        ? newPassword.length >= 12
                        : req.id === 'uppercase'
                        ? /[A-Z]/.test(newPassword)
                        : req.id === 'lowercase'
                        ? /[a-z]/.test(newPassword)
                        : req.id === 'number'
                        ? /[0-9]/.test(newPassword)
                        : /[!@#$%^&*]/.test(newPassword);
                      return (
                        <li key={req.id} className={`text-xs flex items-center gap-1 ${met ? 'text-green-600' : 'text-mv-gray-500'}`}>
                          {met ? '✓' : '○'} {req.label}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            <div className="mb-6">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-mv-gray-700 mb-1">
                Bevestig nieuw wachtwoord
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Herhaal je wachtwoord"
                className="w-full px-4 py-2 border border-mv-gray-300 rounded-lg focus:ring-2 focus:ring-mv-blue-600 focus:border-transparent"
                required
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="mt-1 text-sm text-red-600">Wachtwoorden komen niet overeen</p>
              )}
            </div>

            <label className="flex items-start gap-3 cursor-pointer mb-6">
              <input
                type="checkbox"
                checked={logoutOthers}
                onChange={(e) => setLogoutOthers(e.target.checked)}
                className="mt-1 h-4 w-4 text-mv-blue-600 border-mv-gray-300 rounded focus:ring-mv-blue-600"
              />
              <span className="text-sm text-mv-gray-700">
                Log uit op alle andere apparaten
              </span>
            </label>

            <button
              type="submit"
              disabled={resetMutation.isPending || newPassword !== confirmPassword || getPasswordStrength(newPassword) < 5}
              className="w-full py-3 bg-mv-blue-600 text-white font-semibold rounded-lg hover:bg-mv-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {resetMutation.isPending ? 'Opslaan...' : 'Wachtwoord opslaan'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Success Step
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-mv-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-mv-gray-900 mb-2">Wachtwoord gewijzigd!</h1>
          <p className="text-mv-gray-600 mb-8">
            Je wachtwoord is succesvol gewijzigd. Je kunt nu inloggen met je nieuwe wachtwoord.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-mv-blue-600 text-white font-semibold rounded-lg hover:bg-mv-blue-900 transition-colors"
          >
            Naar inloggen
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // Expired Step - SCR-803 UX Intent: error_handling: blocking
  if (step === 'expired') {
    return (
      <div className="min-h-screen bg-mv-gray-100 flex items-center justify-center px-4">
        <BlockingErrorState
          title="Link verlopen"
          description="Deze reset link is verlopen of al gebruikt. Vraag een nieuwe link aan."
          variant="validation"
          actions={[
            {
              label: 'Nieuwe link aanvragen',
              onClick: () => setStep('request'),
              primary: true,
            },
            {
              label: 'Terug naar inloggen',
              href: '/login',
            },
          ]}
        />
      </div>
    );
  }

  // Error Step - generic error state
  if (step === 'error') {
    return (
      <div className="min-h-screen bg-mv-gray-100 flex items-center justify-center px-4">
        <BlockingErrorState
          title="Er is iets misgegaan"
          description="We konden je verzoek niet verwerken. Probeer het later opnieuw."
          variant="server"
          showSupportLink
          actions={[
            {
              label: 'Opnieuw proberen',
              onClick: () => setStep('request'),
              primary: true,
            },
            {
              label: 'Terug naar inloggen',
              href: '/login',
            },
          ]}
        />
      </div>
    );
  }

  return null;
}
